import { TFileSchema } from '@conversed/shared/src/central/types/common.type';
import { z } from 'zod';
import { partialWithoutDefaults } from '../../rest/zod.utils';
import { HotTopicsMode, HotTopicsPeriod } from './platform-settings.enums';

// A topic description is concatenated into the classifier system prompt for every
// topic on every classification, and the call bills its word count. This cap keeps
// the prompt bounded. Shared by the frontend input maxLength so the two never drift.
export const HOT_TOPIC_DESCRIPTION_MAX_LENGTH = 200;

export const HotTopicWithRelatedSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  percentage: z.number().default(0),
  conversations: z
    .array(
      z.object({
        _id: z.string(),
        question: z.string(),
        answer: z.string(),
        isIntentRedirect: z.boolean().optional(),
      }),
    )
    .optional(),
});

export const PlatformSettingsSchema = z.object({
  companyId: z.string(),
  aiAccuracy: z
    .number()
    .min(0, { message: 'AI accuracy must be at least 0' })
    .max(100, { message: 'AI accuracy must be at most 100' })
    .default(0),
  aiAccuracyDate: z.string().default(''),
  certified: z.boolean().default(false),
  updatedBy: z.string().optional(),
  botLogo: z.union([z.string(), TFileSchema]).optional(),
  botName: z.string().optional(),
  hotTopicsEnabled: z.boolean().default(false),
  timeSavedEnabled: z.boolean().default(false),
  aiAccuracyBenchmarkEnabled: z.boolean().default(true),
  aiAccuracyBenchmarkValue: z.number().min(0).max(100).default(75),
  aiChartShowSubjective: z.boolean().default(false),
  aiChartShowCombined: z.boolean().default(false),
  aiChartShowObjective: z.boolean().default(true),
  leadGenerationBlockId: z.string().default(''),
  hotTopicsPeriod: z
    .enum(
      Object.values(HotTopicsPeriod) as [HotTopicsPeriod, ...HotTopicsPeriod[]],
    )
    .default(HotTopicsPeriod.DAILY),
  csatOverrideEnabled: z.boolean().default(false),
  csatScore: z
    .number()
    .min(0, { message: 'CSAT score must be at least 0' })
    .max(100, { message: 'CSAT score must be at most 100' })
    .default(0),
  ticketDeflectionOverrideEnabled: z.boolean().default(false),
  ticketDeflectionRatio: z
    .number()
    .min(0, { message: 'Ticket deflection ratio must be at least 0' })
    .max(100, { message: 'Ticket deflection ratio must be at most 100' })
    .default(0),
  knowledgeFreshnessThresholdDays: z
    .object({
      warning: z.number().int().positive().default(7),
      urgent: z.number().int().positive().default(14),
      critical: z.number().int().positive().default(30),
    })
    .default({ warning: 7, urgent: 14, critical: 30 }),
});

const UpdatePlatformSettingsFields = PlatformSettingsSchema.pick({
  aiAccuracy: true,
  aiAccuracyDate: true,
  certified: true,
  updatedBy: true,
  botLogo: true,
  botName: true,
  hotTopicsEnabled: true,
  timeSavedEnabled: true,
  aiAccuracyBenchmarkEnabled: true,
  aiAccuracyBenchmarkValue: true,
  aiChartShowSubjective: true,
  aiChartShowCombined: true,
  aiChartShowObjective: true,
  hotTopicsPeriod: true,
  csatOverrideEnabled: true,
  csatScore: true,
  ticketDeflectionOverrideEnabled: true,
  ticketDeflectionRatio: true,
  knowledgeFreshnessThresholdDays: true,
});

/**
 * Four settings pages write this one document (Home, CSAT, Hot Topics, A.I.
 * Knowledge Hub) and the provider persists with `$set`, so an omitted field has
 * to stay omitted - otherwise each page silently resets what the others own.
 *
 * `.partial()` is NOT enough here: every field above carries a `.default()`, and
 * zod keeps applying it through the optional wrapper. `partialWithoutDefaults`
 * strips the default first.
 */
export const UpdatePlatformSettingsSchema = partialWithoutDefaults(
  UpdatePlatformSettingsFields,
);

// * Each CSAT entry pairs one question block with its thumbs-up and thumbs-down buttons.
// * Multiple entries are required when a client runs separate flows (revisions) each with
// * their own block and button ids. Counts from all entries are summed into one score.
export const CsatEntrySchema = z
  .object({
    csatBlockId: z.string().min(1),
    thumbsUpBlockId: z.string().min(1),
    thumbsDownBlockId: z.string().min(1),
  })
  .refine(
    (entry) =>
      !entry.thumbsUpBlockId ||
      !entry.thumbsDownBlockId ||
      entry.thumbsUpBlockId !== entry.thumbsDownBlockId,
    { message: 'CSAT thumbs-up and thumbs-down must be different buttons' },
  );

// * CSAT config lives in Core app_config (read/written via the dedicated csat-config
// * endpoint), not in platform_settings - mirroring how hot topics keep their config
// * in Core as the single source of truth.
export const CsatConfigResponseSchema = z.object({
  csatEntries: z.array(CsatEntrySchema).default([]),
});

export const UpdateCsatConfigSchema = CsatConfigResponseSchema;

// * Review A.I. Agent thresholds, as whole percentages. Stored in Core app_config because the
// * nightly pre-approval runs there, so each company's Core database holds its own values.
export const ReviewAgentConfigSchema = z.object({
  similarityThresholdPercent: z.number().int().min(0).max(100),
  maxPreApprovalPercent: z.number().int().min(0).max(100),
});

// Hot topics configuration stored in Core (app_config + hot_topics collection).
// Separate from platform_settings to keep mode and predefined topics in Core as source of truth.
export const HotTopicsConfigResponseSchema = z.object({
  mode: z.enum(HotTopicsMode),
  predefinedTopics: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string().optional(),
    }),
  ),
  // Topics discovered by the LLM in AI mode - created by Core at call time, so
  // they cannot be added here, only edited or deleted. The description is optional
  // and admin-authored, the same as a predefined topic.
  aiTopics: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string().optional(),
    }),
  ),
});

// Only the mode is written here. Predefined topics are managed per-topic through
// the dedicated upsert and delete endpoints against the Core hot_topics
// collection, which owns the topic shape.
export const UpdateHotTopicsConfigSchema = z.object({
  mode: z.enum(HotTopicsMode),
});

// One predefined topic upsert (add or edit) against the Core collection. The
// name identifies the topic; description is optional and sets the classifier
// steering definition.
export const UpsertHotTopicSchema = z.object({
  name: z.string(),
  description: z.string().max(HOT_TOPIC_DESCRIPTION_MAX_LENGTH).optional(),
});

// Update a single topic's description by id, leaving its name and source untouched.
// Used for AI topics, whose description is admin-authored like a predefined one but
// cannot go through the name-keyed upsert without flipping the topic to PREDEFINED.
export const UpdateHotTopicDescriptionSchema = z.object({
  id: z.string(),
  description: z.string().max(HOT_TOPIC_DESCRIPTION_MAX_LENGTH),
});

// Super-admin-only request to re-run the live classifier against every llm_usages
// row in the given UTC day. `date` is the calendar day to regenerate, formatted
// as YYYY-MM-DD - the backend expands it to [00:00, next-day 00:00) UTC.
export const RegenerateHotTopicsRequestSchema = z.object({
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be in YYYY-MM-DD format'),
});

export const RegenerateHotTopicsResponseSchema = z.object({
  totalScanned: z.number().int().nonnegative(),
  totalReassigned: z.number().int().nonnegative(),
  totalUnchanged: z.number().int().nonnegative(),
  totalFailed: z.number().int().nonnegative(),
});

export const BackfillSentimentRequestSchema = z.object({
  days: z.number().int().positive().max(365).default(7),
});

// The backfill runs in the background, so the response only acknowledges that it started
// and how many rows were queued - final classified/failed counts are logged server-side.
export const BackfillSentimentResponseSchema = z.object({
  started: z.boolean(),
  totalScanned: z.number().int().nonnegative(),
});
